import {Injectable} from '@angular/core';
import {environment} from '../../../../../environments/environment';
import * as dayjs from 'dayjs';
import {ScgHttpWrapper} from '@app/core/injectables/scg-http-wrapper';
import {debounce, filter, multicast, refCount, repeatWhen, takeUntil, tap} from 'rxjs/operators';
import {BehaviorSubject, EMPTY, merge, Observable, timer, of} from 'rxjs';
import {DeviceType} from '@app/core/models/device';
import {NGXLogger} from 'ngx-logger';
import {DeviceService} from '@app/core/services/device.service';
import { EventHelperService } from '@app/core/services/event-helper.service';
import { AuthService } from '@app/core/auth/auth.service';
import {AppWeb} from '@app/core/models/app';

@Injectable({
  providedIn: 'root'
})
export class ApiStatusInfoService {

  /**
   * Multicasted observables which emit when the statusInfo of a device changes/is refreshed.
   * Because they are based on BehaviorSubjects, the last status value will always be saved
   * (the initial null value is not emitted).
   */
  private statusInfoObservables: {
    [deviceId: string]: Observable<StatusInfoCb|StatusInfoCc|null>|{[username: string]: Observable<StatusInfoCb|StatusInfoCc|null> }
  } = {};

  // Service part emptying remainingInfo subscription (e.g. extension), sender is in extension
  private msgClearRemainingInfo = new BehaviorSubject<boolean>(false);
  currentMessage = this.msgClearRemainingInfo.asObservable();

  changeClearRemainingInfo(message: boolean) {
    this.msgClearRemainingInfo.next(message);
  }

  constructor(
    private scgHttp: ScgHttpWrapper,
    private log: NGXLogger,
    private logger: NGXLogger,
    private auth: AuthService,
    private device: DeviceService,
    private eventHelper: EventHelperService
  ) { }

  /**
   * Needs to be called at the start of the app.
   */
  public init() {
    this.auth.onLogout.subscribe(() => {
      this.statusInfoObservables = {};
    });
  }

  /**
   * Emits if statusInfo changed (for CB devices, username may be omitted).
   *
   * If no statusInfo was loaded (cached) yet for a device/user, it may take a while until the data is fetched and emitted.
   */
  public statusInfoChange(deviceId: string, username?: string): Observable<StatusInfoCb|StatusInfoCc> {
    let observable: Observable<StatusInfoCb|StatusInfoCc|null>;
    if (username != null && username !== '') {
      observable = this.statusInfoObservables[deviceId]?.[username];
    } else {
      observable = this.statusInfoObservables[deviceId] as Observable<StatusInfoCb|StatusInfoCc|null>;
    }

    if (observable) {
      // Filter possible initial null value
      // (cached value of observable may be null if first request was started but not finished due to error or interruption)
      return observable.pipe(filter(statusInfo => statusInfo !== null));
    } else {
      const device = this.device.devices.find(dev => dev.id === deviceId);
      if (!device) {
        this.logger.error('Cannot track status info change because the given deviceId does not exist');
        return EMPTY;
      } else if (device.type === DeviceType.CC && (username == null || username === '')) {
        this.logger.error('Cannot track status info change because no username was given for CC device');
        return EMPTY;
      }

      const statusInfoChangeSubject = new BehaviorSubject<StatusInfoCb|StatusInfoCc|null>(null);

      const statusInfoCbSource = this.getStatusInfoCb(deviceId).pipe(
        repeatWhen(notifier => notifier.pipe(debounce(() => merge(timer(20000), this.eventHelper.untilChanged)))),
        takeUntil(this.auth.onLogout)
      );

      const statusInfoCcSource = this.getStatusInfoCc(deviceId, username).pipe(
        repeatWhen(notifier => notifier.pipe(debounce(() => merge(timer(20000), this.eventHelper.untilChanged)))),
        takeUntil(this.auth.onLogout)
      );

      // Automatically subscribe and unsubscribe to source Observable based on whether there are any subscribers
      let refCounted: Observable<StatusInfoCb|StatusInfoCc|null>;
      if (device.type === DeviceType.CB) {
        refCounted = statusInfoCbSource
          .pipe(
            multicast(statusInfoChangeSubject),
            refCount()
          );

        this.statusInfoObservables[deviceId] = refCounted;
      } else if (device.type === DeviceType.CC) {
        refCounted = statusInfoCcSource
          .pipe(
            multicast(statusInfoChangeSubject),
            refCount()
          );

        this.statusInfoObservables[deviceId] ??= {};
        this.statusInfoObservables[deviceId][username] = refCounted;
      }

      // Filter initial null value
      return refCounted.pipe(filter(statusInfo => statusInfo !== null));
    }
  }

  public getStatusInfoCb(deviceId?: string) {
    const postdata = {browsertime: dayjs().format('YYYY-MM-DD HH:mm:ss')};
    return  this.scgHttp.withDevice(deviceId).withoutSync()
      .post<StatusInfoCb|StatusInfoCc>(environment.apiUrl + 'cbsync/v2/statusinfo', postdata);
  }

  public getStatusInfoCc(deviceId, username: string) {
    const postdata = {browsertime: dayjs().format('YYYY-MM-DD HH:mm:ss')};
    return this.scgHttp.withDevice(deviceId).withUsername(username).withoutSync()
      .post<StatusInfoCb|StatusInfoCc>('ccusage/v2/statusinfo', postdata);
  }


  public undoBlockCb(deviceId: string): Observable<any> {
    const postdata = {untilaction : 0};
    return this.scgHttp.withDevice(deviceId).post(environment.apiUrl + 'cbsettings', postdata)
      .pipe(tap(() => this.eventHelper.untilChanged.next(0)));
  }

  public undoBlockCC(deviceId: string, username: string): Observable<any> {
    const postdata = {onetimelocklevel : 0};
    return this.scgHttp.withDevice(deviceId).withUsername(username).post('ccsettings', postdata)
      .pipe(
        tap(() => {
          this.eventHelper.untilChanged.next(0);
        })
      );
  }
}

export interface StatusInfoCc {
  apps?: any[];
  deviceSerial?: string;
  deviceStatus?: 'n/a'|string;
  deviceTimeWarning?: boolean;
  extTime?: number;
  hasRoaming?: boolean;
  hasVacationExtension?: boolean;
  isInUse?: boolean;
  lastAppName?: string;
  lastAzDateTime?: string;
  lastPkgName?: string;
  lastRoamingAzDateTime?: string;
  licenceIssue?: boolean;
  manufacturer?: 'n/a'|string;
  totalDayRemaining?: number;
  totalDayRemainingReasonId?: number;
  totalSecondsRoamingToday?: number;
  totalSecondsToday?: number;
  untilAction?: number;
  untilDateTime?: string;
}

export interface StatusInfoCb {
  apps?: any[];
  deviceSerial?: string;
  deviceStatus?: 'n/a'|string;
  deviceTimeWarning?: boolean;
  extTime?: number;
  hasRoaming?: boolean;
  hasRoamingTimesForPeriod?: boolean;
  isInUse?: boolean;
  lastAppName?: string;
  lastAzDateTime?: string;
  lastPkgName?: string;
  lastRoamingAzDateTime?: string;
  licenceIssue?: boolean;
  manufacturer?: 'n/a'|string;
  totalDayRemaining?: number;
  totalDayRemainingReasonId?: number;
  totalSecondsRoamingToday?: number;
  totalSecondsToday?: number;
  totalSecondsLocal?: number;
  untilAction?: number;
  untilDateTime?: string;
}
